import { SortableHeader } from '@/components/search/SortableHeader'
import React, { useEffect, useMemo, useState } from 'react'
import { useModal } from '@/components/modal/Modal'
import { StaticPaginatedTable } from '@/components/search/StaticPaginatedTable'
import { Spinner } from '@/components/spinner'
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout'
import { BackButton } from '@/components/layout/NavBar'
import { useAppContext } from '@/context/app'
import { Menu, MenuPopupBody } from '@/popups/menupopup/menu'
import { SearchInput } from '@/components/search/SearchInput'

import {
  InventoryGroupEditPopup,
  InventoryGroupPathLabel,
  InventoryGroupRemovePopup,
  InventoryGroupRow,
  ParentInventoryGroupSelectPopup,
  ROOT_INVENTORY_GROUP_ID,
} from './common'

const InventoryGroupMenuPopup = ({
  onClose,
  row,
  onReload,
}: {
  onClose: () => void
  row: InventoryGroupRow
  onReload: () => void
}) => {
  const { openModal } = useModal()

  const menu: Menu = {
    title: row.name,
    rows: [
      {
        icon: 'svgfont-edit',
        title: 'elements.table.inventory_groups.menu.edit.edit_row',
        action: () => openModal((props) => <InventoryGroupEditPopup {...props} inventoryGroupId={row.id} onSaved={onReload} />),
      },
      {
        icon: 'svgfont-remove',
        title: 'elements.table.inventory_groups.menu.edit.remove',
        action: () => openModal((props) => <InventoryGroupRemovePopup {...props} group={row} onRemoved={onReload} />),
      },
    ],
  }

  return <MenuPopupBody menu={menu} onClose={onClose} />
}

export const CommunalRemanentInventoryGroupsManagerPage = () => {
  const { services, session, translates } = useAppContext()
  const { openModal } = useModal()
  const [groups, setGroups] = useState<InventoryGroupRow[] | null>(null)
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [filtred, setFiltred] = useState(groups)

  const subGroups = useMemo(() => {
    if (!groups) {
      return []
    }
    return groups
      .filter((group) => (group.parentId ?? null) === selectedParentId)
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [groups, selectedParentId])

  useEffect(() => {
    if (!debouncedSearch || debouncedSearch === '') {
      setFiltred(subGroups)
      return
    }
    setFiltred(subGroups?.filter((v) => {
      return v.name.indexOf(debouncedSearch) >= 0
    }) || null)
  }, [debouncedSearch, subGroups])

  const loadGroups = async () => {
    const response = await services.session.fetchWithToken('/api/communal/remanent/inventory-groups')
    if (response.status !== 200) {
      setGroups([])
      return
    }
    const data = await response.json()
    setGroups(Array.isArray(data) ? data : [])
  }

  useEffect(() => {
    loadGroups()
  }, [])

  const selectedParentGroup = useMemo(() => {
    if (selectedParentId === null || !groups) {
      return null
    }
    return groups.find((group) => group.id === selectedParentId) || null
  }, [selectedParentId, groups])

  if (!session || !translates || !groups) {
    return <Spinner />
  }

  return <LoggedInPageLayout
    pageName='inventory-groups-manager'
    services={services}
    session={session}
    navbarLeft={<BackButton services={services} translates={session.translates} />}
  >
    <InventoryGroupPathLabel groups={groups} selectedId={selectedParentId} onClick={(row) => {
      if (selectedParentGroup?.id === row?.id) {
        return openModal((props) => <ParentInventoryGroupSelectPopup
          {...props}
          groups={groups}
          selectedId={row?.id ?? null}
          onApprove={(value) => setSelectedParentId(value)}
        />)
      }
      setSelectedParentId(row ? row.id : ROOT_INVENTORY_GROUP_ID)
    }} />

    <StaticPaginatedTable<InventoryGroupRow>
      sortValues={{
        name: row => row.name
      }}
      data={filtred || []}
      translates={translates}
      toolboxLeft={() => <>
        <div
          className='tool'
          onClick={() => openModal((props) => <InventoryGroupEditPopup {...props} defaultParentId={selectedParentId} onSaved={loadGroups} />)}
        >
          <i className='svgfont-add' />
        </div>
      </>}
      toolboxRight={() => <>
        <SearchInput translates={translates} value={search} onChange={setSearch} onDebouncedChange={setDebouncedSearch} />
      </>}
      header={(query, onSort) => <>
        <SortableHeader field='name' query={query} onSort={onSort}>{translates.for('elements.table.inventory_groups.name')}</SortableHeader>
        <div className='hcol'></div>
      </>}
      row={(row) => <>
        <div className='col' onClick={() => setSelectedParentId(row.id)}>{row.name}</div>
        <div className='col icocol' onClick={() => openModal((props) => <InventoryGroupMenuPopup {...props} row={row} onReload={loadGroups} />)}>
          <i className='svgfont-menu' />
        </div>
      </>}
    />
  </LoggedInPageLayout>
}
